import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { Loader2, AlertTriangle, Check, Store, Mail, Lock, User as UserIcon } from 'lucide-react';
import { functions } from '../../config/firebase';
import { useAuth } from '../../hooks/useAuth';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { ShopifyImport } from '../../components/business/ShopifyImport';
import { FASH_LOGO } from '../../constants/brand';

type Status = 'need_auth' | 'claiming' | 'import' | 'error';

/**
 * Finish page for the public Shopify connect flow. Shopify redirects here with a
 * one-time `claim` token. The store owner signs up (or signs in), we claim the
 * pending connection (creating a draft boutique prefilled from their store), and
 * then open the product importer. Route: /connect/shopify/finish
 */
export function ConnectShopifyFinishPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { user, loading: authLoading, signUp, signIn, signInWithGoogle } = useAuth();

  const claimToken = params.get('claim') || '';
  const shop = params.get('shop') || '';
  const storeName = params.get('store') || shop;

  const [status, setStatus] = useState<Status>('claiming');
  const [error, setError] = useState<string | null>(null);
  const [businessId, setBusinessId] = useState<string | null>(null);
  const claimedRef = useRef(false);

  // Auth form
  const [mode, setMode] = useState<'signup' | 'signin'>('signup');
  const [name, setName] = useState(storeName || '');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const runClaim = async () => {
    if (claimedRef.current) return;
    claimedRef.current = true;
    setStatus('claiming');
    setError(null);
    try {
      const claimFn = httpsCallable(functions, 'claimShopifyConnection');
      const result = await claimFn({ claimToken });
      const data = result.data as { businessId: string; storeName: string };
      setBusinessId(data.businessId);
      setStatus('import');
    } catch (err: any) {
      const msg = err?.message || 'Could not finish connecting your store.';
      // If they already claimed (or refresh mid-import), continue to importer.
      if (
        user &&
        (/already been connected|already been used|expired/i.test(msg))
      ) {
        setBusinessId(user.uid);
        setStatus('import');
        return;
      }
      setError(msg);
      setStatus('error');
      claimedRef.current = false;
    }
  };

  // Once authenticated (and we have a claim token), claim automatically.
  useEffect(() => {
    if (authLoading) return;
    if (!claimToken) {
      setError('This connection link is missing or invalid.');
      setStatus('error');
      return;
    }
    if (user && !claimedRef.current) {
      runClaim();
    } else if (!user) {
      setStatus('need_auth');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading, claimToken]);

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      if (mode === 'signup') {
        await signUp(email.trim(), password, name.trim() || storeName || 'My Boutique', 'business');
      } else {
        await signIn(email.trim(), password);
      }
      // The auth effect will pick up the new user and run the claim.
    } catch (err: any) {
      setError(err?.message || 'Authentication failed. Please try again.');
      setSubmitting(false);
    }
  };

  const handleGoogle = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await signInWithGoogle('business');
    } catch (err: any) {
      setError(err?.message || 'Google sign-in failed.');
      setSubmitting(false);
    }
  };

  // ── Import step: open the product importer for the connected store ──
  if (status === 'import' && businessId) {
    return (
      <ShopifyImport
        businessId={businessId}
        onClose={() => navigate('/business')}
        onImportComplete={() => { /* stay until they close */ }}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-purple-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <img src={FASH_LOGO} alt="Fash" className="h-12 w-12 object-contain mx-auto mb-3" />
          <div className="inline-flex items-center gap-2 text-xs font-medium text-primary bg-primary/10 px-3 py-1 rounded-full">
            <Store className="h-3.5 w-3.5" />
            {storeName}
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6">
          {/* Claiming / connecting */}
          {status === 'claiming' && (
            <div className="py-8 text-center space-y-4">
              <Loader2 className="h-8 w-8 text-primary animate-spin mx-auto" />
              <div>
                <p className="text-sm font-semibold text-heading">Connecting {storeName}...</p>
                <p className="text-xs text-body mt-1">Setting up your FASH boutique.</p>
              </div>
            </div>
          )}

          {/* Error */}
          {status === 'error' && (
            <div className="py-6 text-center space-y-4">
              <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center mx-auto">
                <AlertTriangle className="h-7 w-7 text-red-500" />
              </div>
              <div>
                <p className="text-sm font-semibold text-heading">Couldn't connect</p>
                <p className="text-xs text-body mt-1">{error}</p>
              </div>
              <Button variant="outline" className="w-full" onClick={() => navigate('/connect/shopify')}>
                Start over
              </Button>
            </div>
          )}

          {/* Auth needed */}
          {status === 'need_auth' && (
            <div className="space-y-5">
              <div className="text-center">
                <h1 className="text-lg font-serif text-heading">
                  {mode === 'signup' ? 'Create your FASH account' : 'Sign in to finish'}
                </h1>
                <p className="text-xs text-body mt-1">
                  {mode === 'signup'
                    ? `One quick step to link ${storeName} and import your products.`
                    : `Sign in to connect ${storeName} to your boutique.`}
                </p>
              </div>

              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={handleGoogle}
                disabled={submitting}
              >
                Continue with Google
              </Button>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200" />
                </div>
                <div className="relative flex justify-center">
                  <span className="bg-white px-3 text-[11px] text-body uppercase tracking-wider">or</span>
                </div>
              </div>

              <form onSubmit={handleAuth} className="space-y-3">
                {mode === 'signup' && (
                  <div className="relative">
                    <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      type="text"
                      placeholder="Boutique / your name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="pl-9"
                      required
                    />
                  </div>
                )}
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    type="email"
                    placeholder="Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-9"
                    required
                  />
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    type="password"
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-9"
                    minLength={6}
                    required
                  />
                </div>

                {error && (
                  <div className="flex items-start gap-2 p-3 rounded-xl bg-red-50 border border-red-200">
                    <AlertTriangle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-red-700">{error}</p>
                  </div>
                )}

                <Button type="submit" className="w-full" size="lg" disabled={submitting}>
                  {submitting ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Please wait...</>
                  ) : mode === 'signup' ? (
                    'Create account & connect'
                  ) : (
                    'Sign in & connect'
                  )}
                </Button>
              </form>

              <p className="text-center text-xs text-body">
                {mode === 'signup' ? 'Already have an account?' : "Don't have an account?"}{' '}
                <button
                  type="button"
                  onClick={() => {
                    setMode(mode === 'signup' ? 'signin' : 'signup');
                    setError(null);
                  }}
                  className="font-medium text-primary hover:text-primary-hover"
                >
                  {mode === 'signup' ? 'Sign in' : 'Create one'}
                </button>
              </p>

              <div className="flex items-center gap-2 pt-1 text-[11px] text-body">
                <Check className="h-3.5 w-3.5 text-green-500 flex-shrink-0" />
                Your Shopify store is already authorized — this just links it to FASH.
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
